import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import i18n from '@/i18n';
import { apiService, KIOSK_AUTH_TOKEN_CHANGED_EVENT } from '@/services/apiService';
import {
  hasNativeHost,
  sendHeartbeat,
  sendLocale,
  sendSession,
  startKioskHost,
} from '@/platform/kioskHost';

/** How often the page tells the shell it is alive (spec 2026-10-13 §12: silent for 90 s → reload). */
export const KIOSK_HOST_HEARTBEAT_MS = 30_000;

/**
 * Inside the native kiosk shell (spec 2026-10-13 §9, §13): says hello, then keeps the shell told
 * whether a Player is signed in (the shell wipes the machine after a sign-out), which language the
 * shell's own screens should use, and that the page is alive. Does nothing in a normal browser.
 */
export function useKioskHostBridge(): void {
  const { pathname } = useLocation();
  const routeRef = useRef(pathname);
  routeRef.current = pathname;

  useEffect(() => {
    if (!hasNativeHost()) return;
    startKioskHost();

    const reportSession = () => sendSession(apiService.isAuthenticated());
    const reportLocale = (lng: string) => sendLocale(lng);
    const beat = () => sendHeartbeat(routeRef.current, true);

    reportSession();
    reportLocale(i18n.language);
    beat();

    window.addEventListener(KIOSK_AUTH_TOKEN_CHANGED_EVENT, reportSession);
    i18n.on('languageChanged', reportLocale);
    const timer = setInterval(beat, KIOSK_HOST_HEARTBEAT_MS);
    return () => {
      window.removeEventListener(KIOSK_AUTH_TOKEN_CHANGED_EVENT, reportSession);
      i18n.off('languageChanged', reportLocale);
      clearInterval(timer);
    };
  }, []);
}

export default useKioskHostBridge;
